import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, LineChart, Smartphone, QrCode, Target, Clock, MousePointerClick } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CATEGORIES = [
  { icon: QrCode, name: "Design & Scannability", weight: 20 },
  { icon: Target, name: "Placement & Context", weight: 15 },
  { icon: MousePointerClick, name: "CTA & Value", weight: 20 },
  { icon: LineChart, name: "Tracking & Analytics", weight: 15 },
  { icon: Smartphone, name: "Mobile UX", weight: 20 },
  { icon: ShieldCheck, name: "Security & Trust", weight: 10 },
];

export default function Landing() {
  return (
    <div className="grain min-h-screen">
      <Header />

      {/* HERO */}
      <section className="spotlight border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-zinc-400"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#C6F84C]" />
              Free · 12 questions · ~3 minutes
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]"
            >
              Your QR strategy is <br />
              <span className="text-[#C6F84C] lime-text-glow">quietly leaking</span> conversions.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mt-6 text-lg text-zinc-400 max-w-2xl"
            >
              Score how your business uses QR codes against a defensible best-practice
              rubric. Get an on-screen readiness score and a personalised PDF with three
              quick wins tuned to your weakest categories.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/quiz"
                data-testid="landing-start-audit-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-6 py-3.5 transition-colors duration-200"
              >
                Start free audit
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/methodology"
                data-testid="landing-methodology-link"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3.5 transition-colors duration-200"
              >
                See methodology
              </Link>
            </motion.div>

            <div className="mt-10 flex items-center gap-6 text-xs text-zinc-500 uppercase tracking-[0.2em]">
              <div className="flex items-center gap-2"><Clock className="w-3 h-3" /> 3 min</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> No account needed</div>
              <div className="flex items-center gap-2"><LineChart className="w-3 h-3" /> PDF report</div>
            </div>
          </div>

          {/* Score preview card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="relative rounded-2xl border border-white/10 bg-[#121212] p-8 lime-glow"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">Sample readout</div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="font-mono text-7xl font-black text-[#C6F84C] lime-text-glow leading-none">63</div>
                  <div className="text-zinc-500 text-sm mt-2">out of 100 · Competent</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Top fix</div>
                  <div className="text-sm text-white mt-1 max-w-[180px]">Switch from static to dynamic QR codes</div>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                {[
                  ["Design & Scannability", 78],
                  ["Placement & Context", 55],
                  ["CTA & Value", 42],
                  ["Tracking & Analytics", 30],
                  ["Mobile UX", 70],
                  ["Security & Trust", 90],
                ].map(([name, v]) => (
                  <div key={name} className="flex items-center gap-3">
                    <div className="text-xs text-zinc-400 w-40 truncate">{name}</div>
                    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-[#C6F84C]" style={{ width: `${v}%` }} />
                    </div>
                    <div className="font-mono text-xs text-zinc-300 w-8 text-right">{v}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">What we score</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
            Six categories, weighted by real-world impact.
          </h2>
          <p className="mt-4 text-zinc-400">
            The audit isn't about checking boxes. Each category is weighted by how much it actually
            moves scan-to-conversion in the field.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATEGORIES.map(({ icon: Icon, name, weight }) => (
            <div
              key={name}
              className="rounded-xl border border-white/10 bg-[#121212] p-6 hover:border-[#C6F84C] transition-colors duration-200"
              data-testid={`category-card-${name.split(" ")[0].toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-lg bg-[#C6F84C]/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#C6F84C]" />
                </div>
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                  {weight}% weight
                </div>
              </div>
              <div className="mt-6 font-display text-lg font-bold">{name}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-white/5 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Answer 12 questions", d: "Single-select and multi-select. One optional URL check. Neutrally worded — no 'gotcha' framing." },
            { n: "02", t: "See your score instantly", d: "Overall readiness score, proficiency badge, category breakdown, and the top quick win." },
            { n: "03", t: "Get the full PDF report", d: "Drop your email to receive a 4-page personalised PDF with 3 concrete quick wins." },
          ].map((s) => (
            <div key={s.n} className="rounded-xl border border-white/10 bg-[#121212] p-8">
              <div className="font-mono text-[#C6F84C] font-bold text-sm">{s.n}</div>
              <div className="mt-4 font-display text-2xl font-bold tracking-tight">{s.t}</div>
              <p className="mt-3 text-zinc-400">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="rounded-2xl border border-[#C6F84C]/40 bg-[#121212] p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 lime-glow">
          <div className="max-w-xl">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Find out where you're leaking scans.
            </h2>
            <p className="mt-3 text-zinc-400">
              12 questions. 3 minutes. A directional score you can actually act on.
            </p>
          </div>
          <Link
            to="/quiz"
            data-testid="landing-cta-start-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-6 py-3.5 transition-colors duration-200"
          >
            Start free audit
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
